import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../../data/content";

const initial = { nombre: "", email: "", telefono: "", servicio: "", mensaje: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);

  const wa = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hola JAN SMM, me gustaría más información.")}`;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre || !form.mensaje) {
      toast.error("Completa al menos tu nombre y el mensaje.");
      return;
    }

    // Arma el mensaje de WhatsApp con los datos del formulario
    const lineas = [
      "Hola JAN SMM, me gustaría más información.",
      "",
      `*Nombre:* ${form.nombre}`,
      form.email && `*Correo:* ${form.email}`,
      form.telefono && `*Teléfono:* ${form.telefono}`,
      form.servicio && `*Servicio de interés:* ${form.servicio}`,
      "",
      `*Mensaje:*`,
      form.mensaje,
    ].filter(Boolean);

    const url = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(lineas.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");

    toast.success("Abriendo WhatsApp con tu mensaje...");
    setForm(initial);
  };

  const contactItems = [
    { icon: Phone, label: "Teléfono", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phone, href: wa, testid: "contact-whatsapp" },
    { icon: Mail, label: "Correo", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Ubicación", value: CONTACT.address, href: null, testid: "contact-address" },
  ];

  return (
    <section id="contacto" data-testid="contact-section" className="bg-[#0A0A0A] py-24 border-t border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="font-body text-sm tracking-[0.25em] uppercase text-[#FF1616]">Contacto</span>
          <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl tracking-tight mt-3">
            Hablemos de tu equipo
          </h2>
          <p className="font-body text-neutral-400 mt-4 leading-relaxed max-w-md">
            Solicita una cotización o asesoría técnica. Nuestro equipo responde a la brevedad para mantener tu operación
            en marcha.
          </p>

          <div className="mt-8 space-y-3">
            {contactItems.map((c) => {
              const Inner = (
                <div className="flex items-center gap-4 bg-[#141414] border border-[#262626] hover:border-[#FF1616] p-4 transition-colors">
                  <span className="grid place-items-center w-11 h-11 bg-[#FF1616]/10 text-[#FF1616] shrink-0">
                    <c.icon size={20} />
                  </span>
                  <div>
                    <div className="font-body text-xs uppercase tracking-wider text-neutral-500">{c.label}</div>
                    <div className="font-body text-white">{c.value}</div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.testid} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid} className="block">
                  {Inner}
                </a>
              ) : (
                <div key={c.testid} data-testid={c.testid}>{Inner}</div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-[#141414] border border-[#262626] p-7 md:p-9 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field name="nombre" label="Nombre *" value={form.nombre} onChange={handleChange} placeholder="Tu nombre" />
            <Field name="email" type="email" label="Correo *" value={form.email} onChange={handleChange} placeholder="correo@empresa.com" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field name="telefono" label="Teléfono" value={form.telefono} onChange={handleChange} placeholder="(opcional)" />
            <div>
              <label className="font-body text-xs uppercase tracking-wider text-neutral-400">Servicio de interés</label>
              <select
                name="servicio"
                value={form.servicio}
                onChange={handleChange}
                data-testid="contact-input-servicio"
                className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] text-white px-4 py-3 font-body focus:border-[#FF1616] outline-none transition-colors"
              >
                <option value="">Selecciona...</option>
                <option>Motores Deutz</option>
                <option>Motores Cummins</option>
                <option>Motores Mercedes Benz</option>
                <option>Asientos para equipos</option>
                <option>Venta de refacciones</option>
                <option>Otro</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-wider text-neutral-400">Mensaje *</label>
            <textarea
              name="mensaje"
              value={form.mensaje}
              onChange={handleChange}
              rows={4}
              data-testid="contact-input-mensaje"
              placeholder="Cuéntanos qué necesitas..."
              className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] text-white px-4 py-3 font-body focus:border-[#FF1616] outline-none transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            className="w-full flex items-center justify-center gap-2 bg-[#FF1616] hover:bg-[#CC0F0F] text-black font-heading font-bold uppercase tracking-wider py-4 transition-colors"
          >
            <Send size={18} />
            Enviar por WhatsApp
          </button>
          <p className="font-body text-xs text-neutral-500 text-center">
            Al enviar se abrirá WhatsApp con tu mensaje listo para enviar.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ name, label, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="font-body text-xs uppercase tracking-wider text-neutral-400">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`contact-input-${name}`}
      className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] text-white px-4 py-3 font-body focus:border-[#FF1616] outline-none transition-colors"
    />
  </div>
);
